import { useSyncExternalStore } from 'react';

/**
 * « Mode saisie » : clavier virtuel ouvert sur téléphone. Détecté par `window.visualViewport`
 * (zone visible nettement plus basse que la fenêtre, hors zoom) ou par un champ texte qui a le focus
 * sur un écran tactile. Pendant la saisie, la navigation basse disparaît et la barre d'actions des
 * formulaires se réduit à une ligne posée juste au-dessus du clavier (`FormActions`).
 *
 * Le champ actif reste visible : au focus et à chaque redimensionnement de la zone visible, il est
 * ramené au centre (`scrollIntoView({ block: 'center' })`), une fois le clavier stabilisé.
 */
export interface InputModeState {
  typing: boolean;
  /** Haut et hauteur de la zone visible (px, repère de la fenêtre). */
  viewportTop: number;
  viewportHeight: number;
  /** Champ texte actif pendant la saisie (sinon `null`) : une barre d'actions ne flotte que pour son formulaire. */
  active: HTMLElement | null;
}

/** Hauteur de la barre d'actions compacte posée au-dessus du clavier (`FormActions`). */
export const TYPING_BAR_HEIGHT = 56;

/** Écart minimal (px) entre la fenêtre et la zone visible pour conclure à un clavier ouvert. */
export const KEYBOARD_THRESHOLD = 120;

const NON_TEXT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image', 'hidden']);

/** Élément qui ouvre un clavier virtuel (champ texte, zone de texte, contenu éditable). */
export function isTextEntry(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return !NON_TEXT.has(el.type) && !el.readOnly && !el.disabled;
  return el.isContentEditable;
}

/** Dernier type de pointeur utilisé (`touch`, `pen`, `mouse`), relevé à chaque appui. */
let lastPointer: string | null = null;

/**
 * Écran tactile : pointeur principal grossier, n'importe quel pointeur grossier (Samsung Internet,
 * Firefox sur Android, tablettes avec stylet), ou dernier appui au doigt. Sert quand le navigateur
 * redimensionne la page à l'ouverture du clavier (`visualViewport` alors égal à la fenêtre) : un champ
 * texte au focus sur un écran tactile suffit à passer en mode saisie (A5).
 */
export function touchScreen(): boolean {
  const mq = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
  // Téléphone ou tablette : le pointeur principal est le doigt, quel que soit le dernier événement reçu.
  if (mq('(pointer: coarse)')) return true;
  if (lastPointer === 'touch' || lastPointer === 'pen') return true;
  // Ordinateur à écran tactile : seulement si le dernier appui n'était pas à la souris.
  return lastPointer !== 'mouse' && mq('(any-pointer: coarse)');
}

let fullWidth = 0;
let fullHeight = 0;

function compute(): InputModeState {
  if (typeof window === 'undefined') return { typing: false, viewportTop: 0, viewportHeight: 0, active: null };
  const vv = window.visualViewport;
  const height = vv?.height ?? window.innerHeight;
  const top = vv?.offsetTop ?? 0;
  const zoomed = (vv?.scale ?? 1) > 1.01;
  // Hauteur pleine de la fenêtre pour la largeur courante (remise à zéro si la largeur change :
  // rotation, fenêtre redimensionnée sur ordinateur).
  if (window.innerWidth !== fullWidth) {
    fullWidth = window.innerWidth;
    fullHeight = window.innerHeight;
  } else fullHeight = Math.max(fullHeight, window.innerHeight);
  // Clavier : zone visible plus basse que la fenêtre (iOS, Chrome Android), ou fenêtre elle-même
  // rétrécie à largeur égale (Firefox, Samsung Internet, application installée : la page se redimensionne).
  const keyboard =
    (!!vv && !zoomed && window.innerHeight - vv.height > KEYBOARD_THRESHOLD) || fullHeight - window.innerHeight > KEYBOARD_THRESHOLD;
  const el = document.activeElement;
  const typing = isTextEntry(el) && (keyboard || touchScreen());
  return { typing, viewportTop: top, viewportHeight: height, active: typing ? (el as HTMLElement) : null };
}

let state: InputModeState = { typing: false, viewportTop: 0, viewportHeight: 0, active: null };
const listeners = new Set<() => void>();
let detach: (() => void) | null = null;
let scrollTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * Doigt posé sur l'écran : quitter le mode saisie pendant le toucher déplacerait la mise en page (barre
 * d'actions compacte retirée, navigation basse rétablie) et le relâcher tomberait à côté du bouton visé
 * (ex. « OK » d'un prix). On attend la fin du toucher (et le clic qui suit) avant de relire l'état.
 */
let pointerDown = false;
let pendingUpdate = false;

function update(): void {
  const next = compute();
  if (
    next.typing === state.typing &&
    next.viewportTop === state.viewportTop &&
    next.viewportHeight === state.viewportHeight &&
    next.active === state.active
  )
    return;
  state = next;
  document.documentElement.toggleAttribute('data-typing', state.typing);
  for (const l of listeners) l();
}

/** Ramène le champ actif au centre de la zone visible, après la fin du redimensionnement. */
function revealActive(delay: number): void {
  clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => {
    // Jamais pendant un toucher : le bouton visé se déroberait sous le doigt.
    if (pointerDown) return;
    const el = document.activeElement;
    if (!isTextEntry(el)) return;
    // D'abord les conteneurs défilants (feuilles, panneaux)…
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', inline: 'nearest' });
    // …puis la page, par rapport à la zone réellement visible (au-dessus du clavier et de la barre
    // d'actions compacte) : `scrollIntoView` centre sur la fenêtre, que le clavier ne réduit pas (iOS).
    const vv = window.visualViewport;
    if (!vv || typeof window.scrollBy !== 'function') return;
    const r = el.getBoundingClientRect();
    const visibleTop = vv.offsetTop;
    const visibleBottom = vv.offsetTop + vv.height - (state.typing ? TYPING_BAR_HEIGHT : 0);
    const center = (visibleTop + visibleBottom) / 2;
    const delta = r.top + r.height / 2 - center;
    if (Math.abs(delta) > 8) window.scrollBy({ top: delta, left: 0 });
  }, delay);
}

function attach(): () => void {
  const vv = window.visualViewport;
  const onViewport = () => {
    update();
    revealActive(120);
  };
  const onFocusIn = (e: FocusEvent) => {
    const entry = isTextEntry(e.target as Element);
    // Toucher d'un bouton depuis un champ : le focus passe au bouton à l'appui ; quitter le mode saisie
    // tout de suite déplacerait la page et le relâcher tomberait ailleurs (clic perdu).
    if (pointerDown && !entry) {
      pendingUpdate = true;
      return;
    }
    update();
    if (entry) revealActive(320);
  };
  // focusout précède le focusin suivant : on laisse le focus se poser avant de relire l'état.
  const onFocusOut = () => {
    if (pointerDown) pendingUpdate = true;
    else setTimeout(update, 0);
  };
  const onPointer = (e: PointerEvent) => {
    lastPointer = e.pointerType || null;
    pointerDown = true;
    // Un toucher annule le recentrage en attente du champ actif (il déplacerait la cible du toucher).
    clearTimeout(scrollTimer);
  };
  const onPointerEnd = () => {
    pointerDown = false;
    if (!pendingUpdate) return;
    pendingUpdate = false;
    // Après le clic, qui suit le relâcher dans la même séquence d'événements.
    setTimeout(update, 0);
  };
  // Fenêtre redimensionnée (Firefox, Samsung Internet : la page rétrécit avec le clavier).
  const onWindowResize = () => {
    update();
    revealActive(120);
  };
  document.addEventListener('pointerdown', onPointer, true);
  document.addEventListener('pointerup', onPointerEnd, true);
  document.addEventListener('pointercancel', onPointerEnd, true);
  window.addEventListener('resize', onWindowResize);
  vv?.addEventListener('resize', onViewport);
  vv?.addEventListener('scroll', update);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  update();
  return () => {
    vv?.removeEventListener('resize', onViewport);
    vv?.removeEventListener('scroll', update);
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
    document.removeEventListener('pointerdown', onPointer, true);
    document.removeEventListener('pointerup', onPointerEnd, true);
    document.removeEventListener('pointercancel', onPointerEnd, true);
    pointerDown = false;
    pendingUpdate = false;
    window.removeEventListener('resize', onWindowResize);
    clearTimeout(scrollTimer);
    document.documentElement.removeAttribute('data-typing');
    state = { typing: false, viewportTop: 0, viewportHeight: 0, active: null };
  };
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  if (!detach && typeof window !== 'undefined') detach = attach();
  return () => {
    listeners.delete(l);
    if (listeners.size === 0 && detach) {
      detach();
      detach = null;
    }
  };
}

const SERVER: InputModeState = { typing: false, viewportTop: 0, viewportHeight: 0, active: null };

/** État du mode saisie (clavier virtuel), partagé par la coque et les formulaires. */
export function useInputMode(): InputModeState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => SERVER,
  );
}
